import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { UPSERT_USERSETTING } from "src/api/customer";
import { GetUserSetting, GetUserSettingVariables } from "src/api/generated/GetUserSetting";
import { UpsertUsersetting, UpsertUsersettingVariables } from "src/api/generated/UpsertUsersetting";
import { GET_USER_SETTING } from "src/api/user";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";

export enum USER_SETTING {
    CRED_VISA_NOTIFICATION = "CRED.VISANOTIFICATION",
    TICKET_SORT_PREFERENCES = "TICKET.SORTPREFERENCES",
    MFA_HASPERMISSION = "MFA.HASPERMISSION"
}

export interface IUserSettingDetails {
    value: string;
    key: USER_SETTING;
}

export class UserSettingsStore {
    rootStore: RootStore;

    isStoreInitialized: boolean = false;

    userId: string | undefined;
    userSettingsCache: Map<USER_SETTING, string | undefined>;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.userSettingsCache = new Map();

        makeObservable(this, {
            userSettingsCache: observable,

            init: action,
            loadUserSetting: action,
            upsertUserSetting: action,
            getUserSetting: action,
            setUserSetting: action,
            setIsStoreInitialized: action,

            userSetting: computed
        });
    }

    init = async (userId: string) => {
        if (!this.isStoreInitialized) {
            this.userId = userId;

            // Load all user settings and store them in the cache
            for (const userSetting of Object.values(USER_SETTING)) {
                const userSettingValue = await this.loadUserSetting(userSetting);

                runInAction(() => {
                    this.userSettingsCache.set(userSetting, userSettingValue);
                });
            }

            this.setIsStoreInitialized(true);
        }
    };

    setIsStoreInitialized = (isStoreInitialized: boolean) => {
        this.isStoreInitialized = isStoreInitialized;
    };

    loadUserSetting = async (userSetting: USER_SETTING, targetUserId?: string): Promise<string | undefined> => {
        let userSettingValue = undefined;

        const userId = targetUserId ?? this.userId ?? this.rootStore.authStore.user?.userid;

        const { data } = await apolloClientInstance.query<GetUserSetting, GetUserSettingVariables>({
            query: GET_USER_SETTING,
            variables: {
                userid: userId,
                key: userSetting
            },
            // Another user's setting can change behind the cache (MFA bulk), so an explicit target is read fresh.
            fetchPolicy: targetUserId ? "network-only" : undefined
        });

        if (data && data.user_setting.length > 0) {
            userSettingValue = data.user_setting[0]?.value !== null ? data.user_setting[0]?.value : undefined;
        }

        return userSettingValue;
    };

    getUserSetting = (userSetting: USER_SETTING): string | undefined => {
        let userSettingValue = undefined;

        if (this.userSettingsCache.has(userSetting)) {
            userSettingValue = this.userSettingsCache.get(userSetting);
        } else {
            this.loadUserSetting(userSetting);
        }

        return userSettingValue;
    };

    setUserSetting = async (userSettingUpsertObject: IUserSettingDetails): Promise<boolean> => {
        const success = await this.upsertUserSetting(userSettingUpsertObject);

        if (success) {
            runInAction(() => {
                this.userSettingsCache.set(userSettingUpsertObject.key, userSettingUpsertObject.value);
            });
        }

        return success;
    };

    upsertUserSetting = async (userSettingUpsertObject: IUserSettingDetails): Promise<boolean> => {
        let success = true;

        try {
            await apolloClientInstance.mutate<UpsertUsersetting, UpsertUsersettingVariables>({
                mutation: UPSERT_USERSETTING,
                variables: {
                    objects: [
                        {
                            value: userSettingUpsertObject.value,
                            key: userSettingUpsertObject.key
                        }
                    ]
                }
            });
        } catch (error) {
            success = false;
            console.error(`Error updating user-setting: ${userSettingUpsertObject.key}`, error);
        }

        return success;
    };

    get userSetting(): { [key: string]: string | undefined } {
        let userSettingsObject = {};

        for (const userSetting of Object.values(USER_SETTING)) {
            userSettingsObject = {
                ...userSettingsObject,
                [userSetting]: this.userSettingsCache.get(userSetting)
            };
        }

        return userSettingsObject;
    }
}
